import React, { lazy, Suspense, useEffect, useLayoutEffect, useState } from 'react';

import TaMoAkiLogo from '@/components/branding/TaMoAkiLogo';
import { SEO } from '@/components/seo/SEO';
import { UnifiedSearchBar } from '@/components/search/UnifiedSearchBar';
import AccessibilityToolbar from '@/components/ui/AccessibilityToolbar';
import { Button } from '@/components/ui/button';
import { Reveal } from '@/components/ui/reveal';
import { MontarQuandoPerto } from '@/components/ui/MontarQuandoPerto';
// 07/10/2026: secções abaixo do topo carregam e montam só quando estão perto do ecrã.
const TestimonialsSection = lazy(() => import('@/components/landing/TestimonialsSection'));
const PartnersSection = lazy(() => import('@/components/landing/PartnersSection'));
const NewsletterModal = lazy(() => import('@/components/landing/NewsletterModal'));
const Footer = lazy(() => import('@/components/landing/Footer'));
const FAQSection = lazy(() => import('@/components/landing/FAQSection'));
const CategoriasEmDestaque = lazy(() => import('@/components/landing/CategoriasEmDestaque'));
const BenefitsSection = lazy(() => import('@/components/landing/BenefitsSection'));
const BecomeProviderSection = lazy(() => import('@/components/landing/BecomeProviderSection'));
const AppDownloadSection = lazy(() => import('@/components/landing/AppDownloadSection'));
import SkipLink from '@/components/ui/SkipLink';
import {
    ArrowRight,
    CheckCircle,
    Clock,
    Menu,
    Shield,
    Sparkles,
    Star,
    MessageCircle,
    TrendingUp,
    Users,
    X,
    Zap
} from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { track, EVENTOS } from '@/lib/analytics';
import { linkPedidoWhatsApp } from '@/lib/whatsappTaMoAki';


const Index: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openNewsletterModal, setOpenNewsletterModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [vagasFundador, setVagasFundador] = useState<number | null>(null);

  // O topo estático do index.html (aparece antes do JavaScript, 07/10/2026)
  // sai no mesmo frame em que esta página é desenhada — sem ecrã em branco.
  useLayoutEffect(() => {
    document.getElementById('topo-estatico')?.remove();
  }, []);

  // Contador do Programa Fundador (vagas restantes dos primeiros 100).
  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase.rpc('programa_fundador_vagas_restantes');
        if (!error && data) setVagasFundador((data as { restantes: number }).restantes);
      } catch {
        // Contador é não-crítico; se falhar, o banner mostra o CTA normal.
      }
    })();
  }, []);

  // Melhorar navegação por teclado
  useEffect(() => {
    // Garantir que pelo menos um elemento seja focável na página
    const firstFocusableElement = document.querySelector('a, button, input, [tabindex]:not([tabindex="-1"])') as HTMLElement;
    if (firstFocusableElement) {
      firstFocusableElement.setAttribute('tabindex', '0');
    }
    
    // Animação de entrada
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950 font-['Inter',system-ui,-apple-system,sans-serif]">
      {/* Skip Links para acessibilidade */}
      <SkipLink href="#main-content">Pular para o conteúdo principal</SkipLink>
      <SkipLink href="#navigation">Pular para a navegação</SkipLink>
      
      <SEO
        title="Início"
        description="Diga o que precisa e a TaMoAki envia o seu pedido aos prestadores de serviços verificados da sua zona, em Luanda. Sem criar conta: confirma o número por WhatsApp."
        keywords={['prestadores de serviços', 'angola', 'profissionais', 'serviços', 'tamoaki', 'luanda', 'contratação']}
        url="/"
        type="website"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'TaMoAki',
          url: 'https://tamoaki.co.ao',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://tamoaki.co.ao/categorias?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      
      {/* Toolbar de Acessibilidade */}
      <AccessibilityToolbar />
      
      {/* Navegação moderna e responsiva */}
      <nav id="navigation" role="navigation" aria-label="Navegação principal" className="glass-panel bg-white/75 dark:bg-slate-900/70 shadow-sm border-b border-border/60 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <TaMoAkiLogo size="md" showText={true} />
            </div>
            
            {/* Menu Desktop */}
            <div className="hidden md:flex items-center space-x-1">
              <a 
                href="/servicos" 
                className="text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                tabIndex={0}
              >
                Serviços
              </a>
              <a 
                href="https://prestador.tamoaki.co.ao/" 
                target="_blank" rel="noopener noreferrer"
                className="text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                tabIndex={0}
              >
                Prestadores
              </a>
              <Link 
                to="/sobre" 
                className="text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                tabIndex={0}
              >
                Sobre
              </Link>
              <Link 
                to="/contato" 
                className="text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                tabIndex={0}
              >
                Contato
              </Link>
              <Link 
                to="/torne-se-prestador" 
                className="text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 hover:bg-amber-500/10 dark:hover:bg-amber-500/15 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                tabIndex={0}
              >
                Ser Prestador
              </Link>
            </div>
            
            {/* Botões Desktop */}
            <div className="hidden md:flex items-center space-x-3">
              <Link 
                to="/login"
                className="text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                tabIndex={0}
              >
                Entrar
              </Link>
              <Link 
                to="/register"
                className={`btn-glow bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-lg hover:shadow-xl transform hover:scale-105 ${location.pathname === '/register' ? 'ring-2 ring-blue-300' : ''}`}
                tabIndex={0}
                aria-current={location.pathname === '/register' ? 'page' : undefined}
                aria-label="Ir para página de cadastro"
              >
                Cadastrar
              </Link>
            </div>

            {/* Botão Menu Mobile */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 p-2 rounded-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                aria-label="Abrir menu de navegação"
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Menu Mobile */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-border/60 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <a 
                  href="/servicos" 
                  className="block text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-3 py-2 rounded-lg text-base font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Serviços
                </a>
                <a 
                  href="https://prestador.tamoaki.co.ao/" 
                  target="_blank" rel="noopener noreferrer"
                  className="block text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-3 py-2 rounded-lg text-base font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Prestadores
                </a>
                <Link 
                  to="/sobre" 
                  className="block text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-3 py-2 rounded-lg text-base font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sobre
                </Link>
                <Link 
                  to="/contato" 
                  className="block text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-3 py-2 rounded-lg text-base font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contato
                </Link>
                <Link 
                  to="/torne-se-prestador" 
                  className="block text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 px-3 py-2 rounded-lg text-base font-semibold transition-all duration-300 hover:bg-amber-500/10 dark:hover:bg-amber-500/15 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Ser Prestador
                </Link>
                <div className="border-t border-gray-200 pt-3 mt-3">
                  <Link 
                    to="/login"
                    className="block text-foreground/70 hover:text-primary dark:text-slate-200 dark:hover:text-blue-400 px-3 py-2 rounded-lg text-base font-medium transition-all duration-300 hover:bg-primary/5 dark:hover:bg-blue-500/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-blue-500"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Entrar
                  </Link>
                  <Link 
                    to="/register"
                    className={`block bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-3 py-2 rounded-lg text-base font-semibold transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mt-2 ${location.pathname === '/register' ? 'ring-2 ring-blue-300' : ''}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    aria-current={location.pathname === '/register' ? 'page' : undefined}
                  >
                    Cadastrar
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Botão Voltar */}
      {/* <button
        className="mb-8 flex items-center text-blue-600 hover:underline hover:text-blue-800 transition-colors"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-5 h-5 mr-2" /> Voltar
      </button> */}
      {/* Conteúdo Principal */}
      <div id="main-content" role="main">
        {/* Hero Section Modernizado */}
        <section className="relative overflow-hidden" role="banner" aria-labelledby="hero-title">
          {/* Fundo decorativo moderno: grelha + vitrais animados */}
          <div className="absolute inset-0 hero-grid-overlay"></div>
          <div className="hero-blob hero-blob-1 left-[-10%] top-[-10%] h-72 w-72 sm:h-96 sm:w-96 bg-blue-500/40 dark:bg-blue-500/25"></div>
          <div className="hero-blob hero-blob-2 right-[-8%] top-[5%] h-80 w-80 sm:h-[28rem] sm:w-[28rem] bg-orange-500/35 dark:bg-orange-500/25"></div>
          <div className="hero-blob hero-blob-3 bottom-[-15%] left-1/3 h-72 w-72 bg-indigo-400/30 dark:bg-indigo-500/20"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28 tablet-hero-responsive desktop-hero-responsive">
            <div className="text-center">
              {/* Badge de destaque */}
              <Reveal delay={0.05}>
                <div className="inline-flex items-center gap-2 bg-blue-100/70 dark:bg-blue-500/15 text-blue-800 dark:text-blue-300 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8 shadow-sm border border-blue-200/60 dark:border-blue-400/20">
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Prestadores verificados em Luanda</span>
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                <h1 id="hero-title" className="heading-display text-3xl sm:text-5xl md:text-6xl xl:text-7xl text-slate-900 dark:text-slate-50 mb-5 sm:mb-6 tablet-hero-title-responsive desktop-hero-title-responsive">
                  Diga o que precisa.
                  <span className="text-gradient-brand block pb-1">Nós encontramos o prestador.</span>
                </h1>
              </Reveal>

              <Reveal delay={0.2}>
                <p className="text-base sm:text-lg lg:text-xl text-slate-600 dark:text-slate-300 mb-3 sm:mb-4 max-w-3xl mx-auto leading-relaxed px-4 text-balance tablet-hero-subtitle-responsive desktop-hero-subtitle-responsive">
                  Descreva o serviço e o seu distrito: enviamos o pedido aos prestadores verificados da sua zona.
                </p>
                <p className="text-sm sm:text-base lg:text-lg text-slate-500 dark:text-slate-400 mb-6 sm:mb-8 max-w-2xl mx-auto font-medium px-4">
                  Sem criar conta: só confirma o seu número por WhatsApp.
                </p>
              </Reveal>

              {/* Ação principal = Pedido (DEC-011). Até 05/10/2026 era
                  "Começar Agora" (registo) e o pedido era um link pequeno:
                  em 30 dias, ~117 visitantes, 2 abriram /pedir, 0 pedidos. */}
              <Reveal delay={0.22}>
                <div className="flex flex-col items-center gap-3 px-4 mb-8 sm:mb-10">
                  <Link to="/pedir?origem=hero" className="w-full sm:w-auto">
                    <Button
                      size="lg"
                      className="btn-glow w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 sm:px-10 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-xl shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 group"
                    >
                      <span className="flex items-center justify-center space-x-2">
                        <span>Pedir um serviço</span>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </Button>
                  </Link>
                  <a
                    href={linkPedidoWhatsApp()}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => track(EVENTOS.pedidoWhatsapp, { origem: 'hero' })}
                    // 15/10/2026: de link pequeno a botão — em Luanda pede-se por
                    // WhatsApp, e o bot já cria o pedido com profissão e zona.
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-green-600 px-8 py-3 text-base font-semibold text-white shadow-lg transition-colors hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:px-10 sm:text-lg"
                  >
                    <MessageCircle className="h-5 w-5" aria-hidden="true" />
                    Pedir pelo WhatsApp
                  </a>
                </div>
              </Reveal>

              {/* Pesquisa em destaque — deixa o visitante ver serviços/prestadores
                  reais antes de precisar de criar conta. Reaproveita o mesmo
                  componente já usado em /explore e /servicos (páginas públicas). */}
              <Reveal delay={0.24}>
                <div className="max-w-2xl mx-auto mb-10 sm:mb-12 px-4">
                  <UnifiedSearchBar
                    placeholder="Pesquisar categorias, serviços, prestadores..."
                    variant="expanded"
                    showFilters={false}
                  />
                  <p className="mt-3 text-center text-sm text-slate-500 dark:text-slate-400">
                    Ou pesquise e escolha você mesmo um prestador.
                  </p>
                </div>
              </Reveal>

              {/* Estatísticas rápidas em cartões de vidro */}
              <Reveal delay={0.28}>
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-10 sm:mb-12 px-4">
                  {[
                    { icon: CheckCircle, value: 'Verificados', label: 'Prestadores', color: 'text-blue-600 dark:text-blue-400' },
                    { icon: Zap, value: 'Luanda', label: 'Já disponível', color: 'text-amber-500' },
                    { icon: Shield, value: 'Sem conta', label: 'Peça um serviço', color: 'text-green-600 dark:text-green-400' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="glass-panel rounded-2xl px-5 py-3.5 flex items-center gap-3 text-slate-600 dark:text-slate-300"
                    >
                      <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.color}`} />
                      <div className="text-left leading-tight">
                        <div className="font-bold text-base sm:text-lg text-slate-900 dark:text-slate-100">{stat.value}</div>
                        <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">{stat.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>

              <Reveal delay={0.36}>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
                  <Link to="/login" className="w-full sm:w-auto">
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full sm:w-auto border-2 border-border bg-white/60 dark:bg-slate-900/40 hover:border-primary text-foreground hover:text-primary px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                      aria-label="Entrar na minha conta TaMoAki"
                      tabIndex={0}
                    >
                      Já tenho conta
                    </Button>
                  </Link>
                </div>
                <p className="mt-3 text-center text-sm text-slate-500 dark:text-slate-400">
                  Quer acompanhar os seus pedidos e favoritos?{' '}
                  <Link to="/register" className="font-medium text-blue-700 dark:text-blue-300 underline">Criar conta gratuita</Link>
                </p>
              </Reveal>

              {/* Indicador de scroll */}
              <Reveal delay={0.5}>
                <div className="flex justify-center mt-12 sm:mt-16" aria-hidden="true">
                  <div className="scroll-indicator"></div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Categorias com mais prestadores disponíveis (números reais) */}
        <MontarQuandoPerto altura={600}><CategoriasEmDestaque /></MontarQuandoPerto>

        {/* Banner Promocional — Programa Fundador */}
        <section aria-labelledby="promo-title" className="max-w-4xl mx-auto mb-12 px-4">
          <div className={`bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-500/10 dark:to-orange-500/10 border border-amber-200 dark:border-amber-500/30 rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:scale-105 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} role="alert" aria-live="polite">
            <div className="inline-flex items-center space-x-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-full text-lg font-bold mb-4 shadow-lg">
              <span role="img" aria-label="troféu">🏆</span>
              <span id="promo-title">
                {vagasFundador != null
                  ? `Programa Fundador — restam ${vagasFundador} de 100 vagas`
                  : 'Programa Fundador — primeiros 100 prestadores!'}
              </span>
            </div>
            <div className="text-amber-900 dark:text-amber-100 mb-4 text-lg">
              Os primeiros 100 prestadores ficam com <span className="font-bold bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">1.º ano grátis e preço especial</span> e <span className="font-bold">selo especial</span>.
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="https://prestador.tamoaki.co.ao/register?source=clientes">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8 py-4 text-lg font-bold rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group"
                  aria-label="Quero ser prestador fundador TaMoAki"
                >
                  <span className="flex items-center space-x-2">
                    <Zap className="w-5 h-5" />
                    <span>Quero ser prestador</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button>
              </a>
              <Link to="/torne-se-prestador">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-amber-300 dark:border-amber-500/50 bg-white/60 dark:bg-slate-900/40 text-amber-900 dark:text-amber-100 px-8 py-4 text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  aria-label="Ver detalhes do Programa Fundador"
                >
                  Ver detalhes
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Badge de disponibilidade geográfica modernizado */}
        <section aria-label="Disponibilidade geográfica" className="flex justify-center mb-12">
          <div className={`bg-gradient-to-r from-amber-100 to-yellow-100 dark:from-amber-500/15 dark:to-yellow-500/15 text-amber-900 dark:text-amber-200 px-6 py-3 rounded-full text-sm font-semibold shadow-lg border border-amber-200 dark:border-amber-500/30 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4" />
              <span>Disponível apenas para Luanda no momento. Em breve em todas as províncias de Angola!</span>
            </div>
          </div>
        </section>

        {/* Features Section substituída por BenefitsSection */}
        <MontarQuandoPerto altura={900}><BenefitsSection /></MontarQuandoPerto>

        {/* Seção de Depoimentos */}
        <MontarQuandoPerto altura={700}><TestimonialsSection /></MontarQuandoPerto>

        {/* Selo de confiança visual modernizado */}
        <section aria-label="Selo de confiança" className="flex justify-center mb-12">
          <div className={`bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-500/15 dark:to-emerald-500/15 text-green-800 dark:text-green-300 px-8 py-4 rounded-2xl text-lg font-bold shadow-xl border border-green-200 dark:border-green-500/30 transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <span>Cada prestador é verificado antes de entrar na plataforma</span>
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <MontarQuandoPerto altura={800}><FAQSection /></MontarQuandoPerto>

        {/* Seção Torne-se Prestador */}
        <MontarQuandoPerto altura={700}><BecomeProviderSection /></MontarQuandoPerto>

        {/* Seção App Mobile em Breve */}
        <MontarQuandoPerto altura={600}><AppDownloadSection /></MontarQuandoPerto>

        {/* Seção de Parceiros */}
        <MontarQuandoPerto altura={500}><PartnersSection onOpenNewsletter={() => setOpenNewsletterModal(true)} /></MontarQuandoPerto>


        {openNewsletterModal && (<Suspense fallback={null}><NewsletterModal open={openNewsletterModal} onClose={() => setOpenNewsletterModal(false)} /></Suspense>)}
      </div>

      {/* Footer */}
      <MontarQuandoPerto altura={600}><Footer /></MontarQuandoPerto>
    </div>
  );
};

export default Index;
